import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MultiCombobox } from '../ui/multi-combobox';
import { Trash2, Eye, Search, Filter, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

const PRODUCTION_STAGES = [
  'Base Shell Procurement',
  'MS Grinding',
  'Plating',
  'Lathe',
  'Polish',
  'Planning',
  'Engraving',
  'Chroming',
  'Proofing',
  'Dispatch',
];

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [orderStages, setOrderStages] = useState({});
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stages, setStages] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filter states (multi-select arrays; empty = All / no filtering)
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    customer: [],
    circumference: [],
    length: [],
    bore: [],
  });
  
  // Stage status filters
  const [stageFilters, setStageFilters] = useState({});

  // Filter options
  const [filterOptions, setFilterOptions] = useState({
    customers: [],
    circumferences: [],
    lengths: [],
    bores: [],
  });

  useEffect(() => {
    fetchOrders();
  }, [searchQuery]);

  useEffect(() => {
    applyFilters();
  }, [filters, stageFilters, allOrders, orderStages, customers]);

  const fetchOrders = async () => {
    try {
      const [response, customersRes, stagesRes] = await Promise.all([
        apiClient.get(searchQuery ? `/orders?search=${encodeURIComponent(searchQuery)}` : '/orders'),
        apiClient.get('/customers').catch(() => ({ data: [] })),
        apiClient.get('/all-stages').catch(() => ({ data: {} })),
      ]);
      const ordersData = response.data;
      setAllOrders(ordersData);
      setCustomers(customersRes.data || []);
      setOrderStages(stagesRes.data || {});
      
      // Extract unique filter options using customer names
      const custList = customersRes.data || [];
      const getOrderCustomerName = (o) => {
        const c = custList.find(cu => cu.id === o.customer_id);
        return c?.customer_name || c?.parent_customer_name || o.parent_customer_name || o.customer_key || '';
      };
      const customerNames = [...new Set(ordersData.map(o => getOrderCustomerName(o)).filter(Boolean))];
      const circumferences = [...new Set(ordersData.map(o => o.specifications?.circumference).filter(Boolean))].sort((a, b) => a - b);
      const lengths = [...new Set(ordersData.map(o => o.specifications?.length).filter(Boolean))].sort((a, b) => a - b);
      const bores = [...new Set(ordersData.map(o => o.specifications?.bore).filter(Boolean))].sort((a, b) => a - b);
      
      setFilterOptions({ customers: customerNames, circumferences, lengths, bores });
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getOrderCustomerName = (o) => {
    const c = customers.find(cu => cu.id === o.customer_id);
    return c?.customer_name || c?.parent_customer_name || o.parent_customer_name || o.customer_key || '-';
  };

  const applyFilters = () => {
    let filtered = [...allOrders];
    
    if (filters.customer.length) {
      filtered = filtered.filter(o => filters.customer.includes(getOrderCustomerName(o)));
    }
    if (filters.circumference.length) {
      filtered = filtered.filter(o => filters.circumference.some(v => parseFloat(v) === o.specifications?.circumference));
    }
    if (filters.length.length) {
      filtered = filtered.filter(o => filters.length.some(v => parseFloat(v) === o.specifications?.length));
    }
    if (filters.bore.length) {
      filtered = filtered.filter(o => filters.bore.some(v => parseFloat(v) === o.specifications?.bore));
    }

    // Apply stage status filters (multi-select: row matches if its stage
    // status is one of the selected values)
    Object.entries(stageFilters).forEach(([stageName, statusFilter]) => {
      if (statusFilter && statusFilter.length) {
        filtered = filtered.filter(order => {
          const stages = orderStages[order.id] || [];
          const stage = stages.find(s => s.stage_name === stageName);
          return statusFilter.includes(stage?.status);
        });
      }
    });
    
    // Sort newest first
    filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    
    setOrders(filtered);
  };

  const handleViewDetails = async (order) => {
    setSelectedOrder(order);
    try {
      const response = await apiClient.get(`/orders/${order.id}/stages`);
      setStages(response.data);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to fetch order details');
    }
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This will also delete all associated production stages.')) return;

    try {
      await apiClient.delete(`/orders/${orderId}`);
      toast.success('Order deleted successfully');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const clearFilters = () => {
    setFilters({
      customer: [],
      circumference: [],
      length: [],
      bore: [],
    });
    setStageFilters({});
  };

  const hasActiveFilters = Object.values(filters).some(v => v.length > 0) || Object.values(stageFilters).some(v => Array.isArray(v) && v.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-primary font-semibold text-slate-900">Order Management</h2>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="order-search-input"
              placeholder="Search by customer, job name, circumference..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-500 text-white rounded-full">!</span>
            )}
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <Card className="p-4 bg-slate-50 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Filter Orders</h3>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                  <X className="h-4 w-4 mr-1" />
                  Clear all
                </Button>
              )}
            </div>
            
            {/* Basic Filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Customer</label>
                <MultiCombobox
                  options={filterOptions.customers.map(c => ({ value: c, label: c }))}
                  values={filters.customer}
                  onValuesChange={(arr) => setFilters({...filters, customer: arr})}
                  placeholder="All"
                  searchPlaceholder="Search customers..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Circumference</label>
                <MultiCombobox
                  options={filterOptions.circumferences.map(c => ({ value: String(c), label: `${c} mm` }))}
                  values={filters.circumference}
                  onValuesChange={(arr) => setFilters({...filters, circumference: arr})}
                  placeholder="All"
                  searchPlaceholder="Search..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Length</label>
                <MultiCombobox
                  options={filterOptions.lengths.map(l => ({ value: String(l), label: `${l} mm` }))}
                  values={filters.length}
                  onValuesChange={(arr) => setFilters({...filters, length: arr})}
                  placeholder="All"
                  searchPlaceholder="Search..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Bore</label>
                <MultiCombobox
                  options={filterOptions.bores.map(b => ({ value: String(b), label: `${b} mm` }))}
                  values={filters.bore}
                  onValuesChange={(arr) => setFilters({...filters, bore: arr})}
                  placeholder="All"
                  searchPlaceholder="Search..."
                />
              </div>
            </div>

            {/* Stage Status Filters */}
            <div className="border-t border-slate-200 pt-4 mt-4">
              <label className="text-sm font-medium text-slate-700 mb-3 block">Filter by Stage Status</label>
              <div className="grid grid-cols-4 gap-3">
                {PRODUCTION_STAGES.slice(0, 8).map((stage) => (
                  <div key={stage}>
                    <label className="text-xs text-slate-600 mb-1 block truncate" title={stage}>{stage}</label>
                    <MultiCombobox
                      options={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'in_progress', label: 'In Progress' },
                        { value: 'completed', label: 'Done' },
                      ]}
                      values={stageFilters[stage] || []}
                      onValuesChange={(arr) => setStageFilters({...stageFilters, [stage]: arr})}
                      placeholder="Any"
                      searchPlaceholder="Search..."
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                {PRODUCTION_STAGES.slice(8).map((stage) => (
                  <div key={stage}>
                    <label className="text-xs text-slate-600 mb-1 block truncate" title={stage}>{stage}</label>
                    <MultiCombobox
                      options={[
                        { value: 'pending', label: 'Pending' },
                        { value: 'in_progress', label: 'In Progress' },
                        { value: 'completed', label: 'Done' },
                      ]}
                      values={stageFilters[stage] || []}
                      onValuesChange={(arr) => setStageFilters({...stageFilters, [stage]: arr})}
                      placeholder="Any"
                      searchPlaceholder="Search..."
                    />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>

      <Card className="p-6 bg-white rounded-lg border border-slate-200">
        {loading ? (
          <div className="text-center py-8 text-slate-600">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            {hasActiveFilters || searchQuery ? 'No orders match your filters' : 'No orders found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Customer
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Job Name
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Cylinders
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Circ. (mm)
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Length (mm)
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Status
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Date
                  </th>
                  <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm font-medium text-slate-900">
                      {getOrderCustomerName(order)}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600 whitespace-normal break-words min-w-[180px] max-w-[300px]">
                      {order.job_name || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">{order.specifications.nos_of_cylinders}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{order.specifications.circumference}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{order.specifications.length}</td>
                    <td className="py-3 px-4 text-sm">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          order.status === 'pending'
                            ? 'bg-slate-100 text-slate-600 border-slate-200'
                            : order.status === 'in_progress'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}
                      >
                        {order.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {new Date(order.order_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button
                          data-testid={`view-order-${order.id}`}
                          onClick={() => handleViewDetails(order)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid={`delete-order-${order.id}`}
                          onClick={() => handleDelete(order.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent data-testid="order-details-modal" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-primary font-semibold">Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Customer</p>
                  <p className="text-lg font-semibold text-slate-900">{getOrderCustomerName(selectedOrder)}</p>
                </div>
                {selectedOrder.job_name && (
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Job Name</p>
                    <p className="text-base font-semibold text-slate-900 break-words">{selectedOrder.job_name}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium border mt-1 ${
                      selectedOrder.status === 'pending'
                        ? 'bg-slate-100 text-slate-600 border-slate-200'
                        : selectedOrder.status === 'in_progress'
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    }`}
                  >
                    {selectedOrder.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Order Date</p>
                  <p className="text-lg text-slate-900">{new Date(selectedOrder.order_date).toLocaleDateString()}</p>
                </div>
                {selectedOrder.delivery_date && (
                  <div>
                    <p className="text-sm text-slate-500">Delivery Date</p>
                    <p className="text-lg text-slate-900">{new Date(selectedOrder.delivery_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-primary font-semibold text-slate-900 mb-3">Specifications</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-sm text-slate-500">Circumference</p>
                    <p className="text-base font-medium text-slate-900">{selectedOrder.specifications.circumference} mm</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-sm text-slate-500">Length</p>
                    <p className="text-base font-medium text-slate-900">{selectedOrder.specifications.length} mm</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-sm text-slate-500">Number of Cylinders</p>
                    <p className="text-base font-medium text-slate-900">{selectedOrder.specifications.nos_of_cylinders}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-sm text-slate-500">Bore</p>
                    <p className="text-base font-medium text-slate-900">{selectedOrder.specifications.bore} mm</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded">
                    <p className="text-sm text-slate-500">Degree</p>
                    <p className="text-base font-medium text-slate-900">{selectedOrder.specifications.degree}°</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-primary font-semibold text-slate-900 mb-3">Production Stages</h3>
                <div className="space-y-2">
                  {stages.map((stage) => (
                    <div
                      key={stage.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-slate-900 font-medium">{stage.stage_name}</span>
                        {stage.total_completed > 0 && (
                          <span className="text-xs text-slate-500">
                            ({stage.total_completed}/{selectedOrder.specifications.nos_of_cylinders} completed)
                          </span>
                        )}
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          stage.status === 'pending'
                            ? 'bg-slate-100 text-slate-600 border-slate-200'
                            : stage.status === 'in_progress'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}
                      >
                        {stage.status === 'completed' ? 'DONE' : stage.status.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderManagement;
